import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import useIsMobile from "@/lib/useIsMobile";
import usePersistentState from "@/lib/usePersistentState";
import { money } from "@/lib/ui";
import { Loader2, ChevronDown, ExternalLink, Globe, Search, MapPin, CalendarDays, Trophy, ListChecks, UserCircle, ChevronRight } from "lucide-react";

function PrcaCard({ m, nav }) {
  const items = [
    { tab: "schedule", label: "Schedule", icon: CalendarDays },
    { tab: "standings", label: "World Standings", icon: Trophy },
    { tab: "results", label: "Results", icon: ListChecks },
    { tab: "mine", label: "My Entries", icon: UserCircle },
  ];
  return (
    <div className={`rounded-lg border p-4 mb-3 ${m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB] bg-white"}`} data-testid="dir-prca-card">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded bg-[#003087] text-white flex items-center justify-center text-[11px] font-bold">PR</span>
        <span className={`font-display font-bold ${m ? "text-slate-100" : "text-[#14181F]"}`}>PRCA ProRodeo</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.tab} onClick={() => nav(`/prca?tab=${it.tab}`)} data-testid={`dir-prca-${it.tab}`}
              className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${m ? "border-white/10 text-slate-200 hover:bg-white/5" : "border-[#E4E7EB] text-[#14181F] hover:bg-[#FAFBFC]"}`}>
              <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#003087]" /> {it.label}</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Directory() {
  const m = useIsMobile();
  const nav = useNavigate();
  const { country, province } = useFilters();
  const [rodeos, setRodeos] = useState(null);
  const [q, setQ] = useState("");
  const [openCountries, setOpenCountries] = usePersistentState("rp_dir_open_countries", {});

  useEffect(() => { api.get("/rodeos").then((r) => setRodeos(r.data)); }, []);

  const tree = useMemo(() => {
    const t = {};
    (rodeos || []).forEach((r) => {
      const c = r.country || "Canada";
      const p = r.province || "Unspecified";
      if (country && c !== country) return;
      if (province && p !== province) return;
      if (q) {
        const hay = `${r.name} ${r.city || ""} ${p} ${(r.associations || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return;
      }
      t[c] = t[c] || {};
      t[c][p] = t[c][p] || [];
      t[c][p].push(r);
    });
    return t;
  }, [rodeos, country, province, q]);

  const countries = useMemo(() => {
    const cs = Object.keys(tree);
    if ((!country || country === "United States") && !cs.includes("United States")) cs.push("United States");
    return cs.sort();
  }, [tree, country]);
  const total = Object.values(tree).reduce((s, provs) => s + Object.values(provs).reduce((a, arr) => a + arr.length, 0), 0);
  const provOrder = (provs) => Object.keys(provs).sort((a, b) => (a === "Unspecified" ? 1 : b === "Unspecified" ? -1 : a.localeCompare(b)));
  const isOpen = (c) => openCountries[c] !== false; // default open

  // theme
  const t = {
    page: m ? "bg-[#0C1017] min-h-screen pb-24 text-slate-200" : "min-h-screen",
    head: m ? "text-white" : "text-[#14181F]",
    input: m ? "bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F]",
    countryBar: m ? "bg-[#161B22] border-white/10 text-white" : "bg-[#0C1017] text-white border-transparent",
    provHead: m ? "text-slate-400" : "text-slate-500",
    row: m ? "border-white/10 hover:bg-white/5" : "border-[#EEF0F2] hover:bg-[#FAFBFC]",
    name: m ? "text-slate-100" : "text-[#14181F]",
    sub: m ? "text-slate-400" : "text-slate-500",
    chip: m ? "border-white/15 text-slate-300" : "border-[#E4E7EB] text-slate-500",
    link: m ? "text-[#7FB3F0]" : "text-[#2563EB]",
  };

  return (
    <div className={t.page}>
      <div className={`px-4 sm:px-6 py-5 ${m ? "" : "border-b border-[#E4E7EB]"}`}>
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-6 h-6 text-[#C8000F]" />
          <h1 className={`font-display text-2xl font-extrabold tracking-tight ${t.head}`}>Rodeo Directory</h1>
        </div>
        <p className={`text-sm ${t.sub}`}>{rodeos ? `${total} rodeos` : "Loading"} across {countries.length} {countries.length === 1 ? "country" : "countries"}{country ? ` · ${country}` : ""}{province ? ` · ${province}` : ""}</p>
        <div className="relative max-w-md mt-3">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${t.sub}`} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="directory-search" placeholder="Search rodeo, town, association…"
            className={`w-full rounded-lg pl-9 pr-3 py-2.5 text-sm border focus:outline-none focus:border-[#C8000F] ${t.input}`} />
        </div>
      </div>

      {!rodeos ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (total === 0 && !countries.includes("United States")) ? (
        <div className={`text-center py-24 ${t.sub}`}>No rodeos found. Try clearing filters or importing the master database in Settings.</div>
      ) : (
        <div className="px-4 sm:px-6 py-4 space-y-4 max-w-4xl">
          {countries.map((c) => {
            const provs = tree[c] || {};
            const cCount = Object.values(provs).reduce((a, arr) => a + arr.length, 0);
            const open = isOpen(c);
            const isUSA = c === "United States";
            return (
              <div key={c} data-testid={`dir-country-${c.replace(/\s/g, "-")}`}>
                <button onClick={() => setOpenCountries((s) => ({ ...s, [c]: !open }))} data-testid={`dir-country-toggle-${c.replace(/\s/g, "-")}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border font-display font-bold ${t.countryBar}`}>
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C8000F]" /> {c}{isUSA ? <span className="text-xs font-normal opacity-70">· PRCA</span> : null}</span>
                  <span className="flex items-center gap-2 text-sm font-normal opacity-80">{cCount} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
                </button>
                {open && (
                  <div className="mt-2 space-y-3">
                    {isUSA && <PrcaCard m={m} nav={nav} />}
                    {provOrder(provs).map((p) => (
                      <div key={p}>
                        <div className={`text-[11px] font-bold uppercase tracking-wider px-1 mb-1.5 ${t.provHead}`}>{p} · {provs[p].length}</div>
                        <div className={`rounded-lg border ${m ? "border-white/10 bg-[#161B22]" : "border-[#E4E7EB]"} overflow-hidden`}>
                          {provs[p].sort((a, b) => a.name.localeCompare(b.name)).map((r) => (
                            <div key={r.id} className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b last:border-0 ${t.row}`} data-testid={`dir-rodeo-${r.id}`}>
                              <button onClick={() => nav(`/rodeo/${r.id}`)} className="flex-1 min-w-0 text-left">
                                <div className={`text-sm font-semibold truncate ${t.name}`}>{r.name}</div>
                                <div className={`text-xs ${t.sub} flex items-center gap-2 flex-wrap`}>
                                  {r.dates_text && <span>{r.dates_text}</span>}
                                  {r.city && <span className="truncate">· {r.city}</span>}
                                  {!r.listing && <span className="text-[#E68A2E] font-medium">· {money(r.added_money)} Added</span>}
                                </div>
                              </button>
                              <div className="flex items-center gap-2 shrink-0">
                                {(r.associations || []).slice(0, 1).map((a) => <span key={a} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${t.chip}`}>{a}</span>)}
                                {r.website_url && (
                                  <a href={r.website_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`dir-link-${r.id}`}
                                    className={`p-1.5 rounded-md hover:bg-white/10 ${t.link}`} title="Official website"><ExternalLink className="w-4 h-4" /></a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
